import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, Tenant } from '@/types';

interface ImpersonationState {
  isImpersonating: boolean;
  originalToken: string | null;
  originalRefreshToken: string | null;
  originalTokenExpiresAt: number | null;
  impersonatedTenantName: string | null;
}

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  tokenExpiresAt: number | null; // Timestamp en ms
  user: User | null;
  tenant: Tenant | null;
  isAuthenticated: boolean;
  impersonation: ImpersonationState;
  setAuth: (token: string, refreshToken: string, expiresIn: number, user: User) => void;
  setTokens: (token: string, refreshToken: string, expiresIn: number) => void;
  setTenant: (tenant: Tenant | null) => void;
  updateUser: (updates: Partial<User>) => void;
  logout: () => void;
  startImpersonation: (newToken: string, newRefreshToken: string, expiresIn: number, tenantName: string) => void;
  stopImpersonation: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      tokenExpiresAt: null,
      user: null,
      tenant: null,
      isAuthenticated: false,
      impersonation: {
        isImpersonating: false,
        originalToken: null,
        originalRefreshToken: null,
        originalTokenExpiresAt: null,
        impersonatedTenantName: null,
      },
      setAuth: (token, refreshToken, expiresIn, user) =>
        set({
          token,
          refreshToken,
          tokenExpiresAt: Date.now() + expiresIn * 1000,
          user,
          isAuthenticated: true,
          // Limpiar el tenant de la sesión anterior: si no, queda el persistido
          // (localStorage) y se muestran datos de OTRA veterinaria hasta el re-fetch.
          tenant: null,
          impersonation: {
            isImpersonating: false,
            originalToken: null,
            originalRefreshToken: null,
            originalTokenExpiresAt: null,
            impersonatedTenantName: null,
          },
        }),
      setTokens: (token, refreshToken, expiresIn) =>
        set({
          token,
          refreshToken,
          tokenExpiresAt: Date.now() + expiresIn * 1000,
        }),
      setTenant: (tenant) => set({ tenant }),
      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),
      logout: () =>
        set({
          token: null,
          refreshToken: null,
          tokenExpiresAt: null,
          user: null,
          tenant: null,
          isAuthenticated: false,
          impersonation: {
            isImpersonating: false,
            originalToken: null,
            originalRefreshToken: null,
            originalTokenExpiresAt: null,
            impersonatedTenantName: null,
          },
        }),
      startImpersonation: (newToken, newRefreshToken, expiresIn, tenantName) => {
        const currentToken = get().token;
        const currentRefreshToken = get().refreshToken;
        const currentTokenExpiresAt = get().tokenExpiresAt;
        set({
          token: newToken,
          refreshToken: newRefreshToken,
          tokenExpiresAt: Date.now() + expiresIn * 1000,
          tenant: null, // Limpiar tenant anterior para forzar re-fetch
          impersonation: {
            isImpersonating: true,
            originalToken: currentToken,
            originalRefreshToken: currentRefreshToken,
            originalTokenExpiresAt: currentTokenExpiresAt,
            impersonatedTenantName: tenantName,
          },
        });
      },
      stopImpersonation: () => {
        const { originalToken, originalRefreshToken, originalTokenExpiresAt } = get().impersonation;
        set({
          token: originalToken,
          refreshToken: originalRefreshToken,
          tokenExpiresAt: originalTokenExpiresAt,
          tenant: null,
          impersonation: {
            isImpersonating: false,
            originalToken: null,
            originalRefreshToken: null,
            originalTokenExpiresAt: null,
            impersonatedTenantName: null,
          },
        });
      },
    }),
    {
      name: 'auth-storage',
    }
  )
);
